// Lanyard API utilities
const DISCORD_ID = '843536174726512642'; // Replace this with your actual Discord ID
const API_URL = `https://api.lanyard.rest/v1/users/${DISCORD_ID}`;
const SOCKET_URL = 'wss://api.lanyard.rest/socket';

// Fetch Discord presence data using REST API
export const fetchDiscordPresence = async () => {
  try {
    // Add unique timestamp to prevent caching
    const timestamp = Date.now();
    const randomParam = Math.random().toString(36).substring(2, 15);
    const url = `${API_URL}?t=${timestamp}&r=${randomParam}`;
    
    // Use fetch options that prevent caching
    const options = {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      mode: 'cors',
      credentials: 'omit' // Faster without credentials
    };
    
    const response = await fetch(url, options);
    
    if (!response.ok) {
      console.error(`Lanyard API HTTP error: ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    
    if (!data.success) {
      console.error('Lanyard API error');
      return null;
    }
    
    return data.data;
  } catch (error) {
    console.error('Error fetching Discord presence:', error);
    return null;
  }
};

// Set up WebSocket connection for real-time updates
export const connectWebSocket = (onUpdate) => {
  let socket = null;
  let heartbeatInterval = null;
  let reconnectAttempts = 0;

  // Cache control for fetch requests
  const fetchOptions = {
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  };

  // Force a fresh fetch when WebSocket fails
  const forceFetch = async () => {
    try {
      const timestamp = Date.now();
      const url = `${API_URL}?t=${timestamp}&bustCache=${Math.random()}`;
      const response = await fetch(url, fetchOptions);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          onUpdate(data.data);
        }
      }
    } catch (error) {
      console.error('Force fetch error:', error);
    }
  };

  const connect = () => {
    try {
      if (socket) {
        socket.close();
      }
      
      socket = new WebSocket(SOCKET_URL);
      
      // Set a high priority for the WebSocket
      if (socket.binaryType) {
        socket.binaryType = 'arraybuffer'; // More efficient for binary data
      }

      socket.onopen = () => {
        reconnectAttempts = 0;
        
        // Force an immediate fresh fetch to ensure we have latest data
        forceFetch();
        
        // Subscribe to updates for a specific user - use the most efficient method
        socket.send(JSON.stringify({
          op: 2,
          d: {
            subscribe_to_id: DISCORD_ID
          },
        }));
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.op) {
            case 1: // Hello
              // Clear any existing heartbeat
              if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
              }
              
              // Send immediate heartbeat
              socket.send(JSON.stringify({ op: 3 }));
              
              // Set up regular heartbeat at specified interval
              const interval = data.d.heartbeat_interval;
              heartbeatInterval = setInterval(() => {
                if (socket && socket.readyState === WebSocket.OPEN) {
                  socket.send(JSON.stringify({ op: 3 }));
                }
              }, interval);
              break;

            case 0: // Event
              if (data.t === 'PRESENCE_UPDATE' && data.d.user_id === DISCORD_ID) {
                // Push the update immediately
                window.requestAnimationFrame(() => {
                  onUpdate(data.d);
                });
              } else if (data.t === 'INIT_STATE' && data.d[DISCORD_ID]) {
                window.requestAnimationFrame(() => {
                  onUpdate(data.d[DISCORD_ID]);
                });
              }
              break;
              
            default:
              // Handle any unexpected op codes
              console.log(`Unhandled WebSocket op code: ${data.op}`);
              break;
          }
        } catch (error) {
          console.error('WebSocket message processing error:', error);
        }
      };

      socket.onclose = () => {
        clearInterval(heartbeatInterval);
        
        // Force a fetch on close to ensure we have fresh data
        forceFetch();
        
        // Reconnect immediately 
        reconnectAttempts++;
        const delay = Math.min(100, reconnectAttempts * 50); // Short delay increases slightly each attempt
        setTimeout(connect, delay);
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Force fetch on error
        forceFetch();
        if (socket) socket.close();
      };
    } catch (error) {
      console.error('Socket setup error:', error);
      // Force fetch on error
      forceFetch();
      setTimeout(connect, 100);
    }
  };

  // Initial connection
  connect();

  // Return function to close the connection
  return () => {
    if (socket) {
      socket.close();
    }
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }
  };
};

// Utils to extract specific information from presence data
export const getStatusColor = (status) => {
  switch (status) {
    case 'online':
      return '#43b581';
    case 'idle':
      return '#faa61a';
    case 'dnd':
      return '#f04747';
    default:
      return '#747f8d'; // offline/invisible
  }
};

export const getStatusText = (status) => {
  switch (status) {
    case 'online':
      return 'Online';
    case 'idle':
      return 'Idle';
    case 'dnd':
      return 'Do Not Disturb';
    default:
      return 'Offline';
  }
};

// Get Spotify details if available
export const getSpotifyInfo = (presence) => {
  if (presence?.spotify) {
    return {
      isPlaying: true,
      song: presence.spotify.song,
      artist: presence.spotify.artist,
      album: presence.spotify.album,
      albumArtUrl: presence.spotify.album_art_url,
      trackId: presence.spotify.track_id
    };
  }
  return { isPlaying: false };
};

// Get current activity details
export const getActivity = (presence) => {
  if (presence?.activities && presence.activities.length > 0) {
    // Filter out spotify which is handled separately
    const nonSpotifyActivities = presence.activities.filter(
      (activity) => activity.name.toLowerCase() !== 'spotify'
    );
    
    if (nonSpotifyActivities.length > 0) {
      return nonSpotifyActivities[0];
    }
  }
  return null;
};

// Fix the export to avoid the ESLint warning
const lanyardUtils = {
  fetchDiscordPresence,
  connectWebSocket,
  getStatusColor,
  getStatusText,
  getSpotifyInfo,
  getActivity
};

export default lanyardUtils; 